import { AnchorHTMLAttributes } from "react";

import NextLink from "next/link";

import cn from "classnames";
import type { UrlObject } from "node:url";

import { brown } from "@/app/font";

export const generateUrl = (nav: NavigationLink): string | null => {
  const url = nav?.link?.url ?? null;
  if (!url) return null;
  if (!nav.link?.queryString) return url;
  const searchParams = new URLSearchParams(nav.link.queryString);
  return `${url}?${searchParams.toString()}`;
};

export const Link = ({
  href,
  children,
  primary,
  secondary,
  border,
  className,
  ...rest
}: LinkProps) => {
  const classNames = cn(
    {
      [`${brown.className} flex items-center justify-center gap-2 rounded-full p-3 text-[18px] font-bold uppercase duration-300 disabled:pointer-events-none disabled:select-none`]:
        primary || secondary,
      "bg-primary text-secondary disabled:bg-greyscale-strength hover:border-secondary focus:border-secondary border border-transparent disabled:text-white":
        primary && !border,
      "border-primary text-primary disabled:border-greyscale-strength disabled:text-greyscale-strength border-2":
        primary && border,
      "bg-secondary disabled:bg-greyscale-strength text-white":
        secondary && !border,
      "border-secondary text-secondary disabled:border-greyscale-strength disabled:text-greyscale-strength border-2 disabled:bg-transparent":
        secondary && border,
    },
    className,
  );

  if (typeof href === "string" && href.startsWith("tel:")) {
    return (
      <a href={href} className={classNames} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <NextLink href={href} {...rest} className={classNames}>
      {children}
    </NextLink>
  );
};

type Url = string | UrlObject;
type OnNavigateEventHandler = (event: { preventDefault: () => void }) => void;
export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: Url;
  as?: Url;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  passHref?: boolean;
  prefetch?: boolean | "auto" | null;
  locale?: string | false;
  legacyBehavior?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onTouchStart?: React.TouchEventHandler<HTMLAnchorElement>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onNavigate?: OnNavigateEventHandler;
  /** Primary button theme */
  primary?: boolean;
  /** Secondary button theme */
  secondary?: boolean;
  /** Secondary button theme */
  border?: boolean;
};

export type NavigationLink = {
  link?: {
    url?: string | null;
    queryString?: string | null;
  } | null;
};
